import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Turnstile } from '@marsidev/react-turnstile';

import validationSchema from './validationSchema';
import { maxLength } from '../../const/formConst';
import { contactFormSubmit } from '../../redux/slices/contactFormSlice';
import './contact-form.scss';

const allowedFileExtensions = [
  'application/vnd.oasis.opendocument.text',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/msword',
  'application/xlsx',
  'application/rtf',
  'application/odt',
  'application/bpmn',
  'text/plain',
  'text/csv',
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
];

const ContactForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((s) => s.contactForm);
  const [uploadedFile, setUploadedFile] = useState([]);
  const [currentNumber, setCurrentNumber] = useState(maxLength);
  const MAX_FILE_SIZE = 5000000;
  const MAX_FILES_COUNT = 1;
  const [isFilesLimited, setFilesLimited] = useState(false);
  const [isMaxSize, setMaxSize] = useState(false);
  const [isSent, setSending] = useState(false);
  const captchaSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const watchedName = watch('name');
  const watchedEmail = watch('email');
  const emptyFields = !watchedName || !watchedEmail;

  useEffect(() => {
    if (!loading && (success || error) && isSent) {
      onClose();
    }
  }, [loading, success, error, isSent]);

  const onSubmit = (data) => {
    const attachment = uploadedFile.length === 0 ? '' : uploadedFile;

    const contactFormData = new FormData();

    contactFormData.append('comments', data.comments);
    contactFormData.append('full_name', data.name);
    contactFormData.append('email', data.email);
    if (attachment) contactFormData.append('attachment', attachment);

    dispatch(contactFormSubmit(contactFormData));
    setSending(true);
  };

  const countCharacters = (e) => {
    const currentLength = e.target.value.length;
    setCurrentNumber(currentLength);
  };

  const getExtension = (fileName) => fileName.split('.').pop().toLowerCase();
  const getFileName = (fileName) => fileName.split('.')[0];

  const validateFile = (type) => {
    return allowedFileExtensions.indexOf(type) !== -1;
  };

  const isAllowedFile = (fileName) => {
    return /\.(rtf|odt|bpmn)$/i.test(fileName);
  };

  const handleRemoveFile = () => {
    if (isFilesLimited) setFilesLimited(false);
    if (isMaxSize) setMaxSize(false);
    setUploadedFile({});
  };

  const handleAddFile = (e) => {
    if (e.target.files.length > MAX_FILES_COUNT) setFilesLimited(true);
    else setFilesLimited(false);

    const file = e.target.files[0];

    if (isMaxSize) setMaxSize(false);
    const filesSize = file.size;

    if (e.target.files.length === MAX_FILES_COUNT) {
      if (isAllowedFile(file.name) || validateFile(file.type)) {
        if (filesSize <= MAX_FILE_SIZE) setUploadedFile(file);
      }
    }

    if (filesSize > MAX_FILE_SIZE) setMaxSize(true);
  };

  return (
    <div className="form-on-pricing">
      <h2 className="form-title">
        <strong>Get in touch</strong> with us
      </h2>
      <form
        className="on-pricing-popup"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="form-group__row">
          <div className="form-group">
            <label htmlFor="name" className="contact-label required">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              placeholder="John"
              className="contact-input"
              {...register('name')}
              maxLength={100}
            />
            {errors.name && (
              <p className="error-message">{errors.name.message}</p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="email" className="contact-label required">
              Email
            </label>
            <input
              type="email"
              id="email"
              placeholder="John.mobbin@gmail.com"
              className="contact-input"
              {...register('email')}
            />
            {errors.email && (
              <p className="error-message">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="comment" className="contact-label">
            Comments
          </label>

          <textarea
            maxLength={maxLength}
            {...register('comments', { onChange: countCharacters })}
            className="contact-textarea"
          />
          <div className="characters-counter">
            {currentNumber}/{maxLength}
          </div>
        </div>

        <div className="send-container">
          <div className="input-file-box">
            <div>
              {!uploadedFile.name ? (
                <label className="label-file">
                  <input
                    {...register('file_name', { onChange: handleAddFile })}
                    type="file"
                    accept=".pdf,.doc,.docx,application/msword"
                    className="input-file"
                    onChange={handleAddFile}
                    multiple={true}
                  />
                  <span className="span">Attach a file</span>
                </label>
              ) : (
                <div className="attached-files">
                  <div className="file  ic-doc-text">
                    <span className="file-name">
                      {getFileName(uploadedFile.name)}
                    </span>
                    <span className="file-ext">
                      .{getExtension(uploadedFile.name)}
                    </span>
                    <button
                      type="button"
                      className="btn-delete-file ic-close-square"
                      title="Delete"
                      aria-label="Delete"
                      onClick={handleRemoveFile}
                    />
                  </div>
                </div>
              )}
              <div className="warnings">
                <p>
                  PDF, DOC, DOCX. <span> Max file size 5MB.</span>
                  {isFilesLimited && (
                    <span className="warning">
                      Only 1 files could be attached.
                    </span>
                  )}
                </p>
              </div>
            </div>
            {/* Max size warning */}
            {isMaxSize && <p className="warning">Max files size, 5MB</p>}
          </div>
          <div className="recaptcha">
            <Turnstile siteKey={captchaSiteKey} />
          </div>
        </div>
        <div className="button-wrapper">
          <button
            type="submit"
            className="primaryFatLink submit-btn"
            disabled={emptyFields || loading}
          >
            Get Your Free Consultation
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
